import { reportError } from "../utils/Instrumentation";
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import {
  View,
  StyleSheet,
  ScrollView,
  Platform,
  KeyboardAvoidingView,
  Text,
  ActivityIndicator,
  TouchableOpacity,
  Animated,
} from "react-native";
import { RootState } from "../store";
import { useDispatch, useSelector } from "react-redux";
import { useSelectedLocation } from "../hooks";
import {
  LeadershipWalkMiscellaneousObservations,
  LeadershipWalkHeader,
  LeadershipWalkRedirectActivity,
  LeadershipWalkRedirectAction,
  LeadershipWalkSignature,
  fetchQuestions,
} from "./leadershipwalk";
import LeadershipWalkErrorModal from "./leadershipwalk/components/LeadershipWalkErrorModal";
import { useTheme } from "../theme";
import {
  selectQuestions,
  selectLoading,
  selectError,
  setLoading,
  setError,
  QuestionsBySection,
  resetLeadershipWalkRedirect,
  selectSelectedQuestions,
} from "../store/slices/leadershipWalkRedirectSlice";
import {
  selectSelectedZone,
  selectSelectedFrequency,
} from "../store/slices/leaderShipWalkSlice";

interface LeaderShipWalkRedirectScreenProps {
  onBackPress: () => void;
  onHomePress?: () => void; // Add optional home navigation handler
  forceDisabled?: boolean;
}

const LeaderShipWalkRedirectScreen: React.FC<
  LeaderShipWalkRedirectScreenProps
> = ({ onBackPress, onHomePress, forceDisabled = false }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  const dispatch = useDispatch();
  const selectedZones = useSelector(selectSelectedZone);

  // Get selected location for location-change detection
  const selectedLocation = useSelectedLocation();

  // Store initial location ID in a ref (stable, no re-render)
  const initialLocationIdRef = useRef(selectedLocation?.id);

  // Keep a stable ref to onBackPress to avoid stale closure / infinite loop
  const onBackPressRef = useRef(onBackPress);
  useEffect(() => { onBackPressRef.current = onBackPress; }, [onBackPress]);

  // Guard: fire redirect at most once
  const hasRedirectedRef = useRef(false);

  // Detect location changes and redirect back to LeadershipWalk Dashboard
  useEffect(() => {
    const initialId = initialLocationIdRef.current;
    if (
      !hasRedirectedRef.current &&
      initialId &&
      selectedLocation?.id &&
      initialId !== selectedLocation.id
    ) {
      hasRedirectedRef.current = true;
      dispatch(resetLeadershipWalkRedirect());
      onBackPressRef.current();
    }
  }, [selectedLocation?.id, dispatch]);

  const selectedFrequency = useSelector(selectSelectedFrequency);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const questions = useSelector(selectQuestions);
  const selectedWalkData = useSelector(selectSelectedQuestions);
  const scrollViewRef = useRef<ScrollView>(null);
  
  // State for scroll to top button
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const scrollButtonOpacity = useRef(new Animated.Value(0)).current;

  // Move the selectors to the component level so they can be accessed in handleValidateForm
  const selectedOptions = useSelector(
    (state: RootState) => state.leadershipWalkRedirect.selectedOptions
  );
  const managerSignature = useSelector(
    (state: RootState) => state.leadershipWalkRedirect.managerSignatureFileName
  );

  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Group questions by section when they change
  const getQuestionsBySection = () => {
    if (
      !questions ||
      !questions.allQuestions ||
      !Array.isArray(questions.allQuestions) ||
      questions.allQuestions.length === 0
    ) {
      // Try to use the questions from the selected walk if available
      if (questions) {
        // Check if questions is already in the format we need
        // Force the order: Safety, People, Accuracy, Reliability, Costs
        const orderedSections = [
          "Safety",
          "People",
          "Accuracy",
          "Reliability",
          "Costs",
        ];
        const hasCorrectFormat = orderedSections.some((section) =>
          Array.isArray((questions as any)[section])
        );
        if (hasCorrectFormat) {
          // Reorder the sections
          const reordered: QuestionsBySection = {};
          orderedSections.forEach((section) => {
            if ((questions as any)[section]) {
              reordered[section] = (questions as any)[section];
            }
          });
          console.log(
            "Using questions directly from Redux state (ordered):",
            reordered
          );
          return reordered;
        }
      }
      return {} as QuestionsBySection;
    }

    // Group questions by their category
    const groupedQuestions: QuestionsBySection = {};
    questions.allQuestions.forEach((question: any) => {
      const category = question.category;
      if (!groupedQuestions[category]) {
        groupedQuestions[category] = [];
      }
      groupedQuestions[category].push(question);
    });

    // Force the order: Safety, People, Accuracy, Reliability, Costs
    const orderedSections = [
      "Safety",
      "People",
      "Accuracy",
      "Reliability",
      "Costs",
    ];
    const reordered: QuestionsBySection = {};
    orderedSections.forEach((section) => {
      if (groupedQuestions[section]) {
        reordered[section] = groupedQuestions[section];
      }
    });
    return reordered;
  };

  const questionsBySection = getQuestionsBySection();
  const sectionKeys = Object.keys(questionsBySection);

  // This effect runs once when component mounts to pre-populate data from an existing walk
  useEffect(() => {
    // selectedWalkData is now obtained from the top-level useSelector hook
    if (selectedWalkData && typeof selectedWalkData === "object") {
      console.log("Pre-populating data from existing walk:", selectedWalkData);

      // Check for walkData
      if (selectedWalkData.walkData) {
        // If we have misc observations, populate them
        if (selectedWalkData.walkData.notes) {
          console.log("Found notes:", selectedWalkData.walkData.notes);
          // Note: The data is already stored in Redux from the LeaderShipWalkScreen handleRowClick
        }

        // If we have manager signature, populate it
        if (selectedWalkData.walkData.managerSignature) {
          console.log(
            "Found manager signature:",
            selectedWalkData.walkData.managerSignature
          );
          // Note: The data is already stored in Redux from the LeaderShipWalkScreen handleRowClick
        }
      }

      // If we have selected options (toolsMapping), pre-select them
      // This is already handled in LeaderShipWalkScreen handleRowClick
    }
  }, [selectedWalkData]);
  const sections = Object.keys(questionsBySection);

  useEffect(() => {
    if (questions && Object.keys(questions).length > 0) {
      // We already have questions loaded in Redux, no need to fetch again
      console.log("Using existing questions from Redux:", questions);
    } else if (selectedZones?.length > 0 && selectedFrequency) {
      // Only fetch questions if we don't already have them
      console.log(
        "Fetching questions for zones:",
        selectedZones,
        "and frequency:",
        selectedFrequency
      );
      const zoneIds = selectedZones
        .map((zone) => String(zone.ZONE_ID))
        .join(",");
      fetchQuestions(
        selectedFrequency,
        zoneIds,
        dispatch,
        (error) => dispatch(setError(error)),
        (loading) => dispatch(setLoading(loading))
      );
    }
  }, [selectedZones, selectedFrequency, questions, dispatch]);

  const handleValidateForm = (): boolean => {
    let isValid = true;
    const errors: string[] = [];

    console.log("Validating form...", {
      selectedOptions: selectedOptions ? selectedOptions.length : 0,
      hasManagerSignature: !!managerSignature,
    });

    // Check if required selections are made
    // if (!selectedOptions || selectedOptions.length === 0) {
    //   errors.push("Please select at least one option");
    //   isValid = false;
    // }

    // Check if manager signature is present
    if (!managerSignature) {
      errors.push("Manager signature is required");
      isValid = false;
    }

    if (!isValid) {
      console.log("Form validation failed:", errors);
      showStatusMessage("error", errors.join("\n"));
      scrollToTop();
    } else {
      console.log("Form validation successful");
    }

    return isValid;
  };

  const showStatusMessage = (type: "success" | "error", message: string) => {
    setStatusMessage({ type, message });
    setTimeout(() => {
      scrollToTop();
    }, 100);

    if (type === "success") {
      setTimeout(() => {
        setStatusMessage(null);
      }, 5000);
    }
  };

  const scrollToTop = () => {
    try {
      if (Platform.OS === "web") {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      } else if (scrollViewRef.current) {
        scrollViewRef.current.scrollTo({
          x: 0,
          y: 0,
          animated: true,
        });
      }
    } catch (scrollError) {
      console.error("Error scrolling to top:", scrollError);
      reportError({
        severity: "ERROR",
        message:
          scrollError instanceof Error
            ? scrollError.message
            : String(scrollError),
        stackTrace:
          scrollError instanceof Error ? scrollError.stack : undefined,
      });
    }
  };

  // Handle scroll events to show/hide scroll to top button
  const handleScroll = (event: any) => {
    const { contentOffset } = event.nativeEvent;
    const shouldShow = contentOffset.y > 200; // Show button after scrolling 200px
    
    if (shouldShow !== showScrollToTop) {
      setShowScrollToTop(shouldShow);
      
      Animated.timing(scrollButtonOpacity, {
        toValue: shouldShow ? 1 : 0,
        duration: 200,
        useNativeDriver: true,
      }).start();
    }
  };

  // Handle scroll events for web
  useEffect(() => {
    if (Platform.OS === "web") {
      const handleWebScroll = () => {
        const shouldShow = window.scrollY > 200;
        
        if (shouldShow !== showScrollToTop) {
          setShowScrollToTop(shouldShow);
          
          Animated.timing(scrollButtonOpacity, {
            toValue: shouldShow ? 1 : 0,
            duration: 200,
            useNativeDriver: true,
          }).start();
        }
      };

      window.addEventListener('scroll', handleWebScroll);
      return () => window.removeEventListener('scroll', handleWebScroll);
    }
  }, [showScrollToTop, scrollButtonOpacity]);

  // Get zones display text for header
  const getZonesSubtitle = () => {
    if (!selectedZones?.length) {
      return "";
    }
    if (selectedZones.length === 1) {
      return `Zone ${selectedZones[0].ZONE_ID}: ${selectedZones[0].ZONE}`;
    }
    return `Multiple Zones Selected: ${selectedZones
      .map((zone) => zone.ZONE)
      .join(", ")}`;
  };

  // Handle back button press with reset
  const handleBackPress = () => {
    // Reset all data in the leadership walk redirect state
    dispatch(resetLeadershipWalkRedirect());
    // Call the original onBackPress passed from props
    onBackPress();
  };

  // Handle home navigation
  const handleHomePress = () => {
    // Reset all data in the leadership walk redirect state
    dispatch(resetLeadershipWalkRedirect());
    // Call the original onHomePress passed from props
    if (onHomePress) {
      onHomePress();
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      style={[
        styles.keyboardView,
        { backgroundColor: colors.background.page },
      ]}
      keyboardVerticalOffset={Platform.OS === "ios" ? 64 : 0}
    >
      <ScrollView
        ref={scrollViewRef}
        style={[
          styles.scrollView,
          { backgroundColor: colors.background.page },
        ]}
        contentContainerStyle={[
          styles.container,
          styles.contentContainer,
          { backgroundColor: colors.background.page },
        ]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={true}
        bounces={false}
        overScrollMode="always"
        onScroll={Platform.OS !== "web" ? handleScroll : undefined}
        scrollEventThrottle={16}
      >
        <View style={styles.innerContainer}>
          {/* Display error message if present */}
          {error && (
            <View style={[styles.statusMessageContainer]}>
              <View
                style={[
                  styles.statusMessage,
                  styles.errorMessage,
                  isDarkMode && {
                    backgroundColor: colors.background.surface,
                    borderColor: colors.border.default,
                    borderLeftColor: colors.status.error,
                  },
                ]}
              >
                <Text
                  style={[
                    styles.errorText,
                    isDarkMode && { color: colors.text.inverse },
                  ]}
                >
                  {error}
                </Text>
              </View>
            </View>
          )}

          {/* Display status message if present */}
          {statusMessage && (
            <View style={[styles.statusMessageContainer]}>
              <View
                style={[
                  styles.statusMessage,
                  statusMessage.type === "error"
                    ? styles.errorMessage
                    : styles.successMessage,
                  isDarkMode && {
                    backgroundColor: colors.background.surface,
                    borderColor: colors.border.default,
                    borderLeftColor:
                      statusMessage.type === "error"
                        ? colors.status.error
                        : colors.brand.primary,
                  },
                ]}
              >
                <Text
                  style={
                    [
                      statusMessage.type === "error"
                        ? styles.errorText
                        : styles.successText,
                      isDarkMode && { color: colors.text.inverse },
                    ]
                  }
                >
                  {statusMessage.message}
                </Text>
              </View>
            </View>
          )}

          <LeadershipWalkHeader
            title="Weekly Leadership Walk"
            subtitle={getZonesSubtitle()}
            logoSource={require("../assets/warehouse_navigator.png")}
            onReturn={handleBackPress}
            onHomePress={handleHomePress}
          />

          {/* Show loading indicator while fetching questions */}
          {loading && (
            <View style={styles.loadingContainer}>
              <ActivityIndicator
                size="large"
                color={isDarkMode ? colors.text.inverse : colors.brand.primary}
              />
              <Text
                style={[
                  styles.loadingText,
                  { color: isDarkMode ? colors.text.inverse : colors.brand.primary },
                ]}
              >
                Loading leadership walk details...
              </Text>
            </View>
          )}

          {/* Display questions by section */}
          {!loading && sectionKeys.length > 0 && (
            <View style={styles.questionsContainer}>
              {sectionKeys.map((section) => {
                if (
                  !questionsBySection[section] ||
                  questionsBySection[section].length === 0
                ) {
                  return null;
                }
                return (
                  <LeadershipWalkRedirectActivity
                    key={section}
                    title={section}
                    options={questionsBySection[section].map((q: any) => ({
                      id:
                        q.questionId?.toString() ||
                        q.ID?.toString() ||
                        q.Id?.toString() ||
                        q.id?.toString(),
                      label: q.Negative_Behavior || q.Observation || q.Question,
                      details:
                        q.Details ||
                        q.details ||
                        q.DETAILS ||
                        q.Details_Description,
                    }))}
                  />
                );
              })}
            </View>
          )}

          {/* Display message if no questions are available */}
          {!loading && sectionKeys.length === 0 && (
            <View
              style={[
                styles.noQuestionsContainer,
                isDarkMode && {
                  backgroundColor: colors.background.surface,
                  borderColor: colors.border.default,
                  borderWidth: 1,
                },
              ]}
            >
              <Text
                style={[
                  styles.noQuestionsText,
                  { color: isDarkMode ? colors.text.inverse : colors.text.secondary },
                ]}
              >
                No questions available. Please check your connection or try
                again later.
              </Text>
            </View>
          )}

          <LeadershipWalkMiscellaneousObservations />
          <LeadershipWalkSignature />
          <LeadershipWalkRedirectAction
            onSave={() => {
              dispatch(resetLeadershipWalkRedirect());
              onBackPress();
            }}
            onComplete={handleValidateForm}
            onBackToObservation={() => {
              dispatch(resetLeadershipWalkRedirect());
              onBackPress();
            }}
            forceDisabled={forceDisabled}
          />
        </View>
        {/* <Text style={styles.footer}>
          © 2025 PepsiCo | Leadership Walk System v1.2
        </Text> */}
      </ScrollView>

      {/* Scroll to Top Button */}
      <Animated.View
        style={[
          styles.scrollToTopButton,
          {
            opacity: scrollButtonOpacity,
            transform: [
              {
                scale: scrollButtonOpacity.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0.8, 1],
                }),
              },
            ],
          },
        ]}
        pointerEvents={showScrollToTop ? "auto" : "none"}
      >
        <TouchableOpacity
          style={[
            styles.scrollToTopTouchable,
            isDarkMode && {
              backgroundColor: colors.background.surface,
              borderColor: colors.border.default,
            },
          ]}
          onPress={scrollToTop}
          activeOpacity={0.7}
        >
          {/* Arrow up icon */}
          <View style={styles.arrowIcon}>
            <Text
              style={[
                styles.arrowText,
                { color: isDarkMode ? colors.text.inverse : colors.brand.primary },
              ]}
            >
              ↑
            </Text>
          </View>
        </TouchableOpacity>
      </Animated.View>

      {/* Centralized Error Modal */}
      <LeadershipWalkErrorModal />
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  keyboardView: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  container: {
    backgroundColor: "#F5F9FF",
    paddingBottom: 50,
    flexGrow: 1,
    alignItems: "stretch",
    paddingHorizontal: Platform.OS === "web" ? 24 : 16,
  },
  contentContainer: {
    padding: 20,
    paddingBottom: 40,
    alignItems: "stretch",
  },
  innerContainer: {
    flex: 1,
  },
  loadingContainer: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    marginTop: Platform.OS === "web" ? 24 : 16,
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: "#0066CC",
  },
  questionsContainer: {
    marginTop: 16,
  },
  noQuestionsContainer: {
    padding: 20,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8f9fa",
    borderRadius: 6,
    marginTop: 16,
  },
  noQuestionsText: {
    fontSize: 16,
    color: "#666",
    textAlign: "center",
  },
  statusMessageContainer: {
    width: "100%",
    marginBottom: 8,
    zIndex: 10,
  },
  statusMessage: {
    padding: 14,
    borderRadius: 6,
    marginBottom: 16,
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    width: "100%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 3,
  },
  errorMessage: {
    backgroundColor: "#f8d7da",
    borderColor: "#f5c6cb",
    borderLeftWidth: 5,
    borderLeftColor: "#dc3545",
  },
  errorText: {
    color: "#721c24",
    fontSize: 14,
    flex: 1,
  },
  successMessage: {
    backgroundColor: "#d4edda",
    borderColor: "#c3e6cb",
    borderLeftWidth: 5,
    borderLeftColor: "#28a745",
  },
  successText: {
    color: "#155724",
    fontSize: 14,
    flex: 1,
  },
  footer: {
    textAlign: "center",
    marginTop: 40,
    color: "#868e96",
    fontSize: 12,
  },
  scrollToTopButton: {
    position: "absolute",
    bottom: Platform.OS === "web" ? 30 : 80,
    right: 20,
    zIndex: 1000,
  },
  scrollToTopTouchable: {
    backgroundColor: "#FFFFFF",
    borderRadius: 28,
    width: 56,
    height: 56,
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
    elevation: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  arrowIcon: {
    width: 20,
    height: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  arrowText: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#0066CC",
    lineHeight: 24,
  },
});

export default LeaderShipWalkRedirectScreen;
